import React, {
  useEffect,
  useCallback,
  useRef,
  forwardRef,
  RefObject,
  ReactNode
} from 'react';
import { Loader } from '../Loader';

type RefProp<T> = RefObject<T | null> | null;

interface EndlessScrollProps {
  items: ReactNode[];
  loading: boolean;
  onLoadMore: () => void;
  isStopLoad: boolean;
  forwardedRef: RefProp<HTMLDivElement>;
  scrollContainer?: 'window' | 'parent';
}

const EndlessScroll: React.FC<
  EndlessScrollProps & {
    Component: React.ComponentType<{ forwardedRef: RefProp<HTMLDivElement> }>;
  }
> = ({
  onLoadMore,
  loading,
  isStopLoad,
  forwardedRef,
  scrollContainer = 'window',
  items,
  Component,
  ...props
}) => {
  const ticking = useRef(false);

  const handleScroll = useCallback(() => {
    if (ticking.current) return;
    ticking.current = true;

    requestAnimationFrame(() => {
      ticking.current = false;

      if (loading || isStopLoad) return;

      if (scrollContainer === 'parent') {
        const el = forwardedRef?.current;
        if (!el) return;

        const threshold = 150;
        const isNearBottom =
          el.scrollTop + el.clientHeight >= el.scrollHeight - threshold;

        if (isNearBottom) {
          onLoadMore();
        }

        return;
      }

      if (!forwardedRef?.current) return;

      const { top, height } = forwardedRef.current.getBoundingClientRect();
      const offset = window.innerHeight * 2;

      if (
        top + height + offset >= 0 &&
        top + height - offset <= window.innerHeight
      ) {
        onLoadMore();
      }
    });
  }, [onLoadMore, loading, isStopLoad, forwardedRef, scrollContainer]);

  useEffect(() => {
    const target =
      scrollContainer === 'parent' ? forwardedRef?.current : window;

    if (!target) return;

    target.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      target.removeEventListener('scroll', handleScroll);
    };
  }, [handleScroll, scrollContainer, forwardedRef]);

  return (
    <>
      <Component {...props} forwardedRef={forwardedRef} />
      <Loader loading={loading && !!items.length && !isStopLoad} endless />
    </>
  );
};

export const withEndlessScroll = <P extends object>(
  Component: React.ComponentType<
    P & { forwardedRef: RefObject<HTMLDivElement> }
  >
) => {
  const ForwardedComponent = forwardRef<
    HTMLDivElement,
    P & Omit<EndlessScrollProps, 'forwardedRef'>
  >((props, ref) => (
    <EndlessScroll
      {...props}
      forwardedRef={typeof ref !== 'function' ? ref : null}
      Component={
        Component as React.ComponentType<{
          forwardedRef: RefProp<HTMLDivElement>;
        }>
      }
    />
  ));

  const name = Component.displayName || Component.name;
  ForwardedComponent.displayName = `withEndlessScroll(${name})`;

  return ForwardedComponent;
};
